// ─────────────────────────────────────────────────────────────
//  AccountSetupForm – oeffentliche, passwortgeschuetzte Account-Setup-Seite.
//
//  Wird ueber /setup/:slug geoeffnet. Der Setup-Manager kommt mit Link +
//  Passwort (Secret Key) rein und hakt die 3 Setup-Phasen Schritt fuer Schritt
//  ab. Jeder Haken landet ueber die Cloud Functions in der GETEILTEN Checkliste
//  am Kundenobjekt (customers/<slug>/checklist) – also exakt dieselbe Quelle wie
//  die Sales Pipeline (Setup-Schritt) und die Research-Seite (Setup-Tab). Liest/
//  schreibt NIE direkt aus der DB.
// ─────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { ref, onValue } from 'firebase/database';
import { auth, database } from '../firebaseConfig';
import {
  ACCOUNT_SETUP_PHASES,
  accountSetupCompletion,
  phaseCompletion,
} from './accountSetupConfig';
import './AccountSetupForm.css';

const GET_FN_URL =
  process.env.REACT_APP_ACCOUNT_SETUP_GET_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/getAccountSetup';
const TOGGLE_FN_URL =
  process.env.REACT_APP_ACCOUNT_SETUP_TOGGLE_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/toggleAccountSetupItem';

// Cryptische „Entschlüssel"-Animation für die Gate-Überschrift (wie im Protocool).
const SCRAMBLE_GLYPHS = '!<>-_\\/[]{}=+*^?#·•01ABCDEF';
const ScrambleText = ({ text }) => {
  const [display, setDisplay] = useState(text);
  useEffect(() => {
    let frame = 0;
    const totalFrames = 46;
    let raf;
    const tick = () => {
      frame += 1;
      const revealed = Math.floor((frame / totalFrames) * text.length);
      const next = text
        .split('')
        .map((ch, i) => {
          if (ch === ' ') return ' ';
          if (i < revealed) return ch;
          return SCRAMBLE_GLYPHS[Math.floor(Math.random() * SCRAMBLE_GLYPHS.length)];
        })
        .join('');
      setDisplay(next);
      if (frame < totalFrames) raf = requestAnimationFrame(tick);
      else setDisplay(text);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [text]);
  return <span className="asf-scramble">{display}</span>;
};

// Holt das Admin-Token (falls eingeloggt) – wartet einmalig auf den Auth-State.
function resolveIdToken() {
  return new Promise((resolve) => {
    const current = auth && auth.currentUser;
    if (current) {
      current.getIdToken().then(resolve).catch(() => resolve(null));
      return;
    }
    const unsub = onAuthStateChanged(auth, (user) => {
      unsub();
      if (user) user.getIdToken().then(resolve).catch(() => resolve(null));
      else resolve(null);
    });
  });
}

const pwStorageKey = (slug) => `as-pw-${slug}`;
const nameStorageKey = (slug) => `as-name-${slug}`;

const fmtTime = (ms) =>
  ms ? new Date(ms).toLocaleString('de-DE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

const AccountSetupForm = () => {
  const { slug } = useParams();

  const [phase, setPhase] = useState('loading'); // loading | gate | form | error
  const [errorMsg, setErrorMsg] = useState('');
  const [gateError, setGateError] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [checklist, setChecklist] = useState({});
  const [activePassword, setActivePassword] = useState('');
  const [doneBy, setDoneBy] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [lastBy, setLastBy] = useState('');
  const [busyItem, setBusyItem] = useState('');
  // Schritte, deren Toggle gerade unterwegs ist – werden vom Live-Listener nicht
  // ueberschrieben, damit der eigene Klick nicht kurz zurueckspringt.
  const pendingRef = useRef({});

  const completion = useMemo(() => accountSetupCompletion(checklist), [checklist]);

  // noindex: diese Seiten sollen nicht in Suchmaschinen landen.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  // Name des Setup-Managers aus dem letzten Mal vorbefüllen.
  useEffect(() => {
    const stored = localStorage.getItem(nameStorageKey(slug)) || '';
    if (stored) setDoneBy(stored);
  }, [slug]);

  const fetchSetup = useCallback(
    async (password) => {
      const token = await resolveIdToken();
      const resp = await fetch(GET_FN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ slug, password: password || '' }),
      });
      const data = await resp.json().catch(() => ({}));
      return { status: resp.status, data };
    },
    [slug]
  );

  const applyData = useCallback((data) => {
    setCompanyName(data.companyName || slug);
    setChecklist(data.checklist || {});
    setUpdatedAt(data.updatedAt || null);
    setLastBy(data.lastBy || '');
  }, [slug]);

  // Erstes Laden: mit gespeichertem Passwort (oder Admin-Token) versuchen.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stored = localStorage.getItem(pwStorageKey(slug)) || '';
      const { status, data } = await fetchSetup(stored);
      if (cancelled) return;
      if (status === 200 && data.ok) {
        applyData(data);
        setActivePassword(stored);
        setPhase('form');
      } else if (status === 401 || status === 403) {
        if (stored) localStorage.removeItem(pwStorageKey(slug));
        setPhase('gate');
      } else if (status === 404) {
        setErrorMsg(data.error || 'Dieses Setup-Protokoll ist nicht verfügbar.');
        setPhase('error');
      } else {
        setErrorMsg(data.error || 'Etwas ist schiefgelaufen. Bitte versuch es erneut.');
        setPhase('error');
      }
    })();
    return () => { cancelled = true; };
  }, [slug, fetchSetup, applyData]);

  // Live-Synchronisation: hört den öffentlichen Spiegel-Knoten ab. Sobald der
  // Admin im Dashboard / auf der Research-Seite (oder ein anderer Setup-Manager)
  // etwas abhakt, aktualisiert sich diese Seite sofort – ohne Reload.
  useEffect(() => {
    if (phase !== 'form') return undefined;
    const node = ref(database, `salesgod-research/setupStatus/${slug}/checklist`);
    const unsub = onValue(node, (snap) => {
      const live = snap.val();
      if (!live || typeof live !== 'object') return;
      setChecklist((prev) => {
        const merged = { ...prev, ...live };
        // Gerade laufende eigene Toggles nicht überschreiben.
        Object.keys(pendingRef.current).forEach((id) => { merged[id] = pendingRef.current[id]; });
        return merged;
      });
    });
    return () => unsub();
  }, [phase, slug]);

  const submitGate = async (e) => {
    e.preventDefault();
    setGateError('');
    const pw = passwordInput.trim();
    if (!pw) { setGateError('Bitte gib den Secret Key ein.'); return; }
    const { status, data } = await fetchSetup(pw);
    if (status === 200 && data.ok) {
      localStorage.setItem(pwStorageKey(slug), pw);
      applyData(data);
      setActivePassword(pw);
      setPhase('form');
    } else if (status === 403) {
      setGateError('Falscher Key. Bitte prüfe ihn und versuch es erneut.');
    } else if (status === 401) {
      setGateError('Secret Key erforderlich.');
    } else {
      setGateError(data.error || 'Etwas ist schiefgelaufen. Bitte versuch es erneut.');
    }
  };

  const toggleStep = useCallback(
    async (stepId, nextDone) => {
      if (busyItem) return;
      setBusyItem(stepId);
      pendingRef.current[stepId] = nextDone;
      // Optimistisch sofort umschalten.
      setChecklist((prev) => ({ ...prev, [stepId]: nextDone }));
      const by = doneBy.trim();
      if (by) localStorage.setItem(nameStorageKey(slug), by);
      try {
        const token = await resolveIdToken();
        const resp = await fetch(TOGGLE_FN_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ slug, password: activePassword || '', itemId: stepId, done: nextDone, by }),
        });
        const data = await resp.json().catch(() => ({}));
        if (resp.status === 200 && data.ok) {
          delete pendingRef.current[stepId];
          setChecklist((prev) => ({ ...prev, ...(data.checklist || {}) }));
          setUpdatedAt(data.updatedAt || Date.now());
          setLastBy(data.lastBy || '');
        } else {
          // Rollback bei Fehler.
          delete pendingRef.current[stepId];
          setChecklist((prev) => ({ ...prev, [stepId]: !nextDone }));
          setErrorMsg(data.error || 'Konnte nicht speichern. Bitte versuch es erneut.');
          setTimeout(() => setErrorMsg(''), 3200);
        }
      } catch (err) {
        delete pendingRef.current[stepId];
        setChecklist((prev) => ({ ...prev, [stepId]: !nextDone }));
        setErrorMsg('Netzwerkfehler. Bitte versuch es erneut.');
        setTimeout(() => setErrorMsg(''), 3200);
      } finally {
        setBusyItem('');
      }
    },
    [busyItem, slug, activePassword, doneBy]
  );

  // ── Guards ────────────────────────────────────────────────────
  if (phase === 'loading') {
    return (
      <div className="asf-wrap">
        <div className="asf-state">
          <div className="asf-spinner" />
          <p className="asf-state-text">Setup-Protokoll wird geladen…</p>
        </div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="asf-wrap">
        <div className="asf-state">
          <h1 className="asf-state-title">Etwas ist schiefgelaufen</h1>
          <p className="asf-state-text">{errorMsg}</p>
        </div>
      </div>
    );
  }

  if (phase === 'gate') {
    return (
      <div className="asf-gate-screen">
        <div className="asf-gate-orbs" aria-hidden="true">
          <span className="asf-gate-orb asf-gate-orb-1" />
          <span className="asf-gate-orb asf-gate-orb-2" />
          <span className="asf-gate-orb asf-gate-orb-3" />
        </div>

        <form className="asf-gate-card" onSubmit={submitGate}>
          <div className="asf-gate-vault" aria-hidden="true">
            <span className="asf-gate-ring asf-gate-ring-1" />
            <span className="asf-gate-ring asf-gate-ring-2" />
            <div className="asf-gate-lock">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="11" width="16" height="9" rx="2" />
                <path d="M8 11V8a4 4 0 0 1 8 0v3" />
              </svg>
            </div>
          </div>

          <span className="asf-gate-eyebrow">Account-Setup-Protokoll</span>
          <h1 className="asf-gate-heading">
            <ScrambleText text="Geschütztes Setup" />
          </h1>
          <p className="asf-gate-text">Bitte gib den Secret Key ein, den du erhalten hast.</p>

          <div className={`asf-gate-field ${gateError ? 'asf-gate-field-error' : ''}`}>
            <span className="asf-gate-field-icon" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="11" width="16" height="9" rx="2" />
                <path d="M8 11V8a4 4 0 0 1 8 0v3" />
              </svg>
            </span>
            <input
              className="asf-gate-field-input"
              type="text"
              autoComplete="off"
              autoFocus
              placeholder="XXXX-XXXX-XXXX-XXXX"
              value={passwordInput}
              onChange={(e) => { setPasswordInput(e.target.value); if (gateError) setGateError(''); }}
            />
          </div>
          {gateError && <span className="asf-gate-field-err">{gateError}</span>}

          <button type="submit" className="asf-gate-cta">
            <span className="asf-gate-cta-label">Entsperren</span>
          </button>

          <span className="asf-gate-foot">Vertraulich · nur für das SalesGod-Team</span>
        </form>
      </div>
    );
  }

  // phase === 'form'
  return (
    <div className="asf-wrap">
      <div className="asf-sheet">
        <header className="asf-header">
          <span className="asf-eyebrow">Account-Setup-Protokoll</span>
          <h1 className="asf-title">Setup für {companyName}</h1>
          <p className="asf-intro">
            Das ist deine Schritt-für-Schritt-Anleitung für das technische SalesGod-Setup. Arbeite die
            drei Phasen der Reihe nach durch: Unter jedem Schritt steht genau, was zu tun ist (inkl. Links).
            Hak einen Schritt ab, sobald er erledigt ist – tippe dazu auf das Kästchen oder den Titel.
            Dein Fortschritt wird sofort gespeichert und ist live im Team Dashboard sichtbar.
          </p>

          <div className="asf-progress">
            <div className="asf-progress-bar">
              <div className="asf-progress-fill" style={{ width: `${completion.pct}%` }} />
            </div>
            <span className="asf-progress-label">{completion.done}/{completion.total} erledigt · {completion.pct}%</span>
          </div>

          <div className="asf-field-name">
            <label className="asf-label">Dein Name</label>
            <input
              className="asf-input"
              type="text"
              value={doneBy}
              placeholder="Wer führt das Setup durch?"
              onChange={(e) => setDoneBy(e.target.value)}
            />
          </div>

          {updatedAt && (
            <p className="asf-meta">Zuletzt aktualisiert {fmtTime(updatedAt)}{lastBy ? ` · ${lastBy}` : ''}</p>
          )}
        </header>

        {ACCOUNT_SETUP_PHASES.map((ph, idx) => {
          const pc = phaseCompletion(ph, checklist);
          const phaseDone = pc.total > 0 && pc.done === pc.total;
          return (
            <section
              key={ph.id}
              className={`asf-phase ${phaseDone ? 'asf-phase-done' : ''}`}
              style={{ '--asf-accent': ph.accent }}
            >
              <div className="asf-phase-head">
                <div className="asf-phase-headtop">
                  <span className="asf-phase-no">Phase {idx + 1}</span>
                  {ph.duration && <span className="asf-phase-dur">{ph.duration}</span>}
                  <span className={`asf-phase-prog ${phaseDone ? 'asf-phase-prog-ok' : ''}`}>{pc.done}/{pc.total}</span>
                </div>
                <h2 className="asf-phase-title">{ph.label}</h2>
                {ph.intro && <p className="asf-phase-intro">{ph.intro}</p>}
              </div>

              <ul className="asf-steps">
                {ph.steps.map((step) => {
                  const done = !!checklist[step.id];
                  return (
                    <li key={step.id} className={`asf-step ${done ? 'asf-step-done' : ''} ${busyItem === step.id ? 'asf-step-busy' : ''}`}>
                      <div className="asf-step-row">
                        <button
                          type="button"
                          className={`asf-check ${done ? 'asf-check-on' : ''}`}
                          role="switch"
                          aria-checked={done}
                          disabled={busyItem === step.id}
                          onClick={() => toggleStep(step.id, !done)}
                          aria-label={done ? 'Als offen markieren' : 'Als erledigt markieren'}
                        >
                          <span className="asf-check-box" aria-hidden="true">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                          </span>
                        </button>
                        <button
                          type="button"
                          className="asf-step-main"
                          disabled={busyItem === step.id}
                          onClick={() => toggleStep(step.id, !done)}
                        >
                          <span className="asf-step-num">Step {step.num}</span>
                          <span className="asf-step-title">{step.title}</span>
                          <span className={`asf-step-state ${done ? 'asf-step-state-done' : ''}`}>
                            {done ? 'Erledigt' : 'Offen'}
                          </span>
                        </button>
                      </div>

                      <div className="asf-step-details">
                        {step.desc && step.desc.length > 0 && (
                          <ul className="asf-step-desc">
                            {step.desc.map((line, i) => (
                              <li key={i}>{line}</li>
                            ))}
                          </ul>
                        )}
                        {step.links && step.links.length > 0 && (
                          <div className="asf-step-links">
                            {step.links.map((l) => (
                              <a key={l.url} className="asf-step-link" href={l.url} target="_blank" rel="noopener noreferrer">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><path d="M15 3h6v6" /><path d="M10 14 21 3" /></svg>
                                {l.label}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}

        <footer className="asf-footer">
          {completion.done === completion.total ? (
            <div className="asf-done">
              <span className="asf-done-check" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
              </span>
              <div className="asf-done-text">
                <span className="asf-done-title">Setup komplett! 🎉</span>
                <span className="asf-done-sub">Alle {completion.total} Schritte erledigt. Das Team sieht den Stand live.</span>
              </div>
            </div>
          ) : (
            <div className="asf-footer-meta">
              Noch {completion.total - completion.done} von {completion.total} Schritten offen. Dein Fortschritt wird automatisch gespeichert.
            </div>
          )}
        </footer>

        {errorMsg && <div className="asf-inline-error">{errorMsg}</div>}
      </div>
    </div>
  );
};

export default AccountSetupForm;
